import { useEffect, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'sonner';
import { Loader2, UploadCloud, FileText, X, Sparkles } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetBody,
  SheetFooter,
  SheetClose,
} from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import {
  useCreateDocumentoFiscal,
  type TipoDocumento,
  type CreateDocumentoFiscalInput,
} from '@/lib/hooks/documentos-fiscais';
import { uploadFile } from '@/lib/hooks/pagamentos';
import { cn, fmtCpfCnpj } from '@/lib/utils';

interface FormValues {
  tipo: TipoDocumento | '';
  numero: string;
  serie: string;
  chaveAcesso: string;
  emitenteCnpj: string;
  emitenteNome: string;
  valorTotal: string;
  dataEmissao: string;
  descricaoServicos: string;
  r2Key: string;
}

interface Props {
  workspaceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const TIPOS: { value: TipoDocumento; label: string }[] = [
  { value: 'NFSE', label: 'NFS-e (Serviços)' },
  { value: 'NFE', label: 'NF-e (Produto)' },
  { value: 'NFAE', label: 'NFA-e (Avulsa)' },
  { value: 'OUTRO', label: 'Outro' },
];

const ACCEPT = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];

const DEFAULT_VALUES: FormValues = {
  tipo: '',
  numero: '',
  serie: '',
  chaveAcesso: '',
  emitenteCnpj: '',
  emitenteNome: '',
  valorTotal: '',
  dataEmissao: '',
  descricaoServicos: '',
  r2Key: '',
};

export function DocumentoFiscalDrawer({ workspaceId, open, onOpenChange }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedName, setUploadedName] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const createMut = useCreateDocumentoFiscal(workspaceId);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ defaultValues: DEFAULT_VALUES });

  useEffect(() => {
    if (!open) {
      reset(DEFAULT_VALUES);
      setUploadedName(null);
      setUploading(false);
    }
  }, [open, reset]);

  const r2Key = watch('r2Key');

  async function handleFile(file: File) {
    if (!ACCEPT.includes(file.type)) {
      toast.error('Formato não suportado. Use PDF, JPG, PNG ou WebP.');
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error('Arquivo excede 15 MB.');
      return;
    }
    setUploading(true);
    try {
      const key = await uploadFile(file, 'notas-fiscais');
      setValue('r2Key', key, { shouldDirty: true });
      setUploadedName(file.name);
      toast.success('Nota fiscal enviada.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha no upload.');
    } finally {
      setUploading(false);
    }
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }

  function removeFile() {
    setValue('r2Key', '', { shouldDirty: true });
    setUploadedName(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  async function onSubmit(values: FormValues) {
    if (!values.tipo) {
      toast.error('Selecione o tipo do documento fiscal.');
      return;
    }
    const payload: CreateDocumentoFiscalInput = {
      tipo: values.tipo,
      emitenteCnpj: values.emitenteCnpj.replace(/\D/g, ''),
      emitenteNome: values.emitenteNome,
      valorTotal: values.valorTotal.replace(',', '.'),
      dataEmissao: values.dataEmissao,
    };
    if (values.numero) payload.numero = values.numero;
    if (values.serie) payload.serie = values.serie;
    if (values.chaveAcesso) payload.chaveAcesso = values.chaveAcesso;
    if (values.descricaoServicos) payload.descricaoServicos = values.descricaoServicos;
    if (values.r2Key) payload.r2Key = values.r2Key;

    try {
      await createMut.mutateAsync(payload);
      toast.success('Documento fiscal cadastrado.');
      onOpenChange(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Falha ao cadastrar documento fiscal.';
      toast.error(msg);
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1 min-h-0">
          <SheetHeader
            eyebrow="Novo lançamento"
            title="Cadastrar documento fiscal"
            description="Suba a nota fiscal ou preencha manualmente. Você pode editar depois."
          />

          <SheetBody className="space-y-5">
            {/* Dropzone */}
            <div>
              <Label className="mb-2 block">Nota fiscal</Label>
              {r2Key ? (
                <div className="flex items-center gap-3 rounded-2 border border-card-stroke bg-card-soft p-3">
                  <div className="grid h-10 w-10 place-items-center rounded-1 bg-mint-soft text-mint-deep">
                    <FileText className="h-4 w-4" strokeWidth={1.8} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-fg truncate">
                      {uploadedName ?? 'arquivo enviado'}
                    </p>
                    <p className="text-[11px] text-fg-muted font-mono truncate">{r2Key}</p>
                  </div>
                  <button
                    type="button"
                    onClick={removeFile}
                    className="grid h-8 w-8 place-items-center rounded-1 text-fg-muted hover:text-fg hover:bg-card"
                    aria-label="Remover"
                  >
                    <X className="h-4 w-4" strokeWidth={1.8} />
                  </button>
                </div>
              ) : (
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                  }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={onDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={cn(
                    'flex flex-col items-center justify-center gap-2 rounded-2 border-2 border-dashed px-4 py-7 cursor-pointer transition-colors',
                    dragOver
                      ? 'border-mint-deep bg-mint-soft/40'
                      : 'border-card-stroke bg-card-soft hover:border-mint-deep/60',
                  )}
                >
                  <div className="grid h-10 w-10 place-items-center rounded-full bg-mint-soft text-mint-deep">
                    {uploading ? (
                      <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} />
                    ) : (
                      <UploadCloud className="h-4 w-4" strokeWidth={1.8} />
                    )}
                  </div>
                  <p className="text-sm font-medium text-fg">
                    {uploading ? 'Enviando…' : 'Arraste ou clique para enviar'}
                  </p>
                  <p className="text-[11px] text-fg-muted">PDF, JPG, PNG ou WebP · até 15 MB</p>
                  <p className="flex items-center gap-1 text-[11px] text-lilac-deep font-medium mt-1">
                    <Sparkles className="h-3 w-3" strokeWidth={2} />
                    IA preencherá os campos em breve
                  </p>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT.join(',')}
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFile(f);
                }}
              />
            </div>

            {/* Tipo */}
            <div>
              <Label className="mb-1.5 block">Tipo de documento</Label>
              <Controller
                control={control}
                name="tipo"
                rules={{ required: 'Obrigatório' }}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione…" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIPOS.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.tipo && (
                <p className="text-[11px] text-warn mt-1">{errors.tipo.message}</p>
              )}
            </div>

            {/* Número / Série */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="numero" className="mb-1.5 block">Número</Label>
                <Input id="numero" placeholder="000001" {...register('numero')} />
              </div>
              <div>
                <Label htmlFor="serie" className="mb-1.5 block">Série</Label>
                <Input id="serie" placeholder="1" {...register('serie')} />
              </div>
            </div>

            {/* Chave de acesso */}
            <div>
              <Label htmlFor="chaveAcesso" className="mb-1.5 block">Chave de acesso</Label>
              <Input
                id="chaveAcesso"
                placeholder="44 dígitos (opcional)"
                {...register('chaveAcesso')}
              />
            </div>

            {/* Emitente */}
            <div>
              <Label htmlFor="emitenteNome" className="mb-1.5 block">Emitente (nome)</Label>
              <Input
                id="emitenteNome"
                placeholder="Ex.: Empresa Fornecedora Ltda"
                {...register('emitenteNome', {
                  required: 'Obrigatório',
                  minLength: { value: 2, message: 'Mínimo 2 caracteres' },
                })}
              />
              {errors.emitenteNome && (
                <p className="text-[11px] text-warn mt-1">{errors.emitenteNome.message}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="emitenteCnpj" className="mb-1.5 block">CNPJ do emitente</Label>
                <Input
                  id="emitenteCnpj"
                  placeholder="00.000.000/0000-00"
                  {...register('emitenteCnpj', {
                    required: 'Obrigatório',
                    setValueAs: (v: string) => fmtCpfCnpj(v),
                    validate: (v) =>
                      v.replace(/\D/g, '').length === 14 || 'CNPJ deve ter 14 dígitos',
                  })}
                />
                {errors.emitenteCnpj && (
                  <p className="text-[11px] text-warn mt-1">{errors.emitenteCnpj.message}</p>
                )}
              </div>
              <div>
                <Label htmlFor="valorTotal" className="mb-1.5 block">Valor total (R$)</Label>
                <Input
                  id="valorTotal"
                  inputMode="decimal"
                  placeholder="0.00"
                  {...register('valorTotal', {
                    required: 'Obrigatório',
                    pattern: { value: /^\d+([.,]\d{1,2})?$/, message: 'Valor inválido' },
                  })}
                />
                {errors.valorTotal && (
                  <p className="text-[11px] text-warn mt-1">{errors.valorTotal.message}</p>
                )}
              </div>
            </div>

            {/* Data de emissão */}
            <div>
              <Label htmlFor="dataEmissao" className="mb-1.5 block">Data de emissão</Label>
              <Input
                id="dataEmissao"
                type="date"
                className="w-[200px]"
                {...register('dataEmissao', { required: 'Obrigatório' })}
              />
              {errors.dataEmissao && (
                <p className="text-[11px] text-warn mt-1">{errors.dataEmissao.message}</p>
              )}
            </div>

            {/* Descrição de serviços */}
            <div>
              <Label htmlFor="descricaoServicos" className="mb-1.5 block">
                Descrição dos serviços
              </Label>
              <Textarea
                id="descricaoServicos"
                placeholder="Descreva os serviços ou produtos da nota…"
                rows={3}
                {...register('descricaoServicos')}
              />
            </div>
          </SheetBody>

          <SheetFooter>
            <SheetClose asChild>
              <Button variant="ghost" type="button">Cancelar</Button>
            </SheetClose>
            <Button variant="primary" type="submit" disabled={isSubmitting || uploading}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} />
                  Salvando…
                </>
              ) : (
                'Cadastrar documento fiscal'
              )}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
